import { ShoppingCart, Trash2, Plus, Minus, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useCart } from '../contexts/CartContext';
import { toast } from 'sonner';

const WHATSAPP_NUMBER = '5591984849479';

export default function CartPage() {
  const { items, updateQuantity, removeItem, clearCart, getTotal, generateWhatsAppMessage } = useCart();

  const handleSendWhatsApp = () => {
    if (items.length === 0) {
      toast.error('Adicione produtos ao carrinho primeiro!');
      return;
    }
    
    const message = generateWhatsAppMessage();
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${message}`;
    window.open(url, '_blank');
    toast.success('Redirecionando para o WhatsApp...');
  };

  const handleClearCart = () => {
    clearCart();
    toast.info('Carrinho limpo!');
  };

  if (items.length === 0) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-4" data-testid="empty-cart">
        <div className="text-center space-y-4">
          <div className="w-20 h-20 mx-auto rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
            <ShoppingCart className="w-10 h-10 text-slate-400" />
          </div>
          <h2 className="text-2xl font-secondary font-bold text-slate-900 dark:text-white">
            Seu carrinho está vazio
          </h2>
          <p className="text-slate-500 dark:text-slate-400">
            Adicione produtos para continuar
          </p>
          <Link to="/">
            <Button className="mt-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg" data-testid="continue-shopping-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Continuar comprando
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="py-8 md:py-12" data-testid="cart-page">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl md:text-4xl font-secondary font-bold text-slate-900 dark:text-white">
            Carrinho
          </h1>
          <Button 
            variant="ghost" 
            onClick={handleClearCart}
            className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
            data-testid="clear-cart-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Limpar
          </Button>
        </div>

        {/* Cart Items */}
        <div className="space-y-4 mb-8">
          {items.map((item) => (
            <div 
              key={item.id} 
              className="cart-item"
              data-testid={`cart-item-${item.id}`}
            >
              {/* Image */}
              <div className="w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-800">
                <img 
                  src={item.image} 
                  alt={item.name}
                  className="w-full h-full object-contain p-2"
                />
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-slate-900 dark:text-white line-clamp-1">
                  {item.name}
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {item.category}
                </p>
                <p className="text-lg font-bold text-blue-600 dark:text-blue-400 mt-1">
                  R$ {item.price.toFixed(2)}
                </p>
              </div>

              {/* Quantity Controls */}
              <div className="flex flex-col items-end gap-2">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => updateQuantity(item.id, item.quantity - 1)}
                    className="quantity-btn"
                    data-testid={`decrease-qty-${item.id}`}
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="w-8 text-center font-medium text-slate-900 dark:text-white" data-testid={`qty-${item.id}`}>
                    {item.quantity}
                  </span>
                  <button
                    onClick={() => updateQuantity(item.id, item.quantity + 1)}
                    className="quantity-btn"
                    data-testid={`increase-qty-${item.id}`}
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <button
                  onClick={() => {
                    removeItem(item.id);
                    toast.info(`${item.name} removido do carrinho`);
                  }}
                  className="text-sm text-red-500 hover:text-red-600 transition-colors"
                  data-testid={`remove-item-${item.id}`}
                >
                  Remover
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Summary */}
        <div className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-slate-600 dark:text-slate-400">Subtotal ({items.length} itens)</span>
            <span className="font-medium text-slate-900 dark:text-white">
              R$ {getTotal().toFixed(2)}
            </span>
          </div>
          <div className="border-t border-slate-200 dark:border-slate-800 pt-4">
            <div className="flex justify-between items-center">
              <span className="text-lg font-semibold text-slate-900 dark:text-white">Total</span>
              <span className="text-2xl font-bold text-slate-900 dark:text-white font-secondary">
                R$ {getTotal().toFixed(2)}
              </span>
            </div>
          </div>

          {/* WhatsApp Button */}
          <Button
            onClick={handleSendWhatsApp}
            className="w-full h-14 bg-[#25D366] hover:bg-[#20BA5A] text-white rounded-full font-bold text-lg shadow-lg shadow-green-500/20 transition-transform hover:scale-[1.02] active:scale-[0.98] whatsapp-btn"
            data-testid="send-whatsapp-btn"
          >
            <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            Enviar pelo WhatsApp
          </Button>

          <p className="text-center text-sm text-slate-500 dark:text-slate-400">
            Ao clicar, você será redirecionado para o WhatsApp
          </p>
        </div>

        {/* Continue Shopping */}
        <div className="mt-6 text-center">
          <Link to="/" className="text-blue-600 dark:text-blue-400 hover:underline font-medium">
            <ArrowLeft className="w-4 h-4 inline mr-1" />
            Continuar comprando
          </Link>
        </div>
      </div>
    </div>
  );
}
